// Battles, Grandia / Final Fantasy VII Remake style. In the open world a fight becomes a circle on the map, and
// everyone in it drops into a small arena (an instance that looks like where you were). In the arena you move
// freely in real time; your time bar fills as you go, and when it's full you act: attack (you dash in and
// strike), cast (stand still for a moment), use an item. Creatures chase whoever has their attention and wind up
// big moves as shapes on the floor that you can step out of. The server runs everything; this file holds the
// numbers and the shapes both sides agree on.

/** The circle on the map: how big, and who may walk in. */
export const CIRCLE_R_PX = 40;
export const MAX_FIGHTERS = 6;                 // players in one circle, party and helpers together (a full party)
/** A world boss fight has room for a whole alliance: three parties of six. */
export const ALLIANCE_FIGHTERS = 18;
/** A world boss's health for this many fighters: about its listed health for a group of eight, more for a full
 *  alliance, much less for a handful (so a few friends can still try). */
export const bossHpFor = (base: number, fighters: number): number => Math.round(base * (0.3 + 0.7 * Math.max(1, fighters) / 8));
/** Party members this close (in tiles) when a fight starts come into it too. */
export const RALLY_TILES = 10;
/** The time bar: 1000 is a full bar; it fills this much every server battle step (10 a second). */
export const ATB_FULL = 1000, ATB_STEP_TICKS = 6;
export const PLAYER_SPEED = 34;                // about three seconds from empty to full
/** You have this long to pick (the bar is paused meanwhile); then you defend. */
export const CHOOSE_TICKS = 60 * 12;

// ---------- the arena ----------
/** A melee hit lands from this close (pixels); further away, you dash in first (up to DASH_PX). */
export const MELEE_PX = 24, DASH_PX = 220;
/** Spells take a moment to cast; moving before it's done interrupts them (nothing is spent). */
export const castTicks = (mp: number) => 36 + Math.min(30, mp * 2);
/** A big move's shape sits on the floor this long before it lands. */
export const TELE_TICKS = 150;
/** Hits from different friends this close together (ticks) chain; a spell inside the window is a Magic Burst. */
export const CHAIN_WINDOW_TICKS = 300;
/** Hitting a creature from behind. */
export const BACK_MULT = 1.25;
/** Stand in the glowing gate this long to get away (not from bosses). */
export const ESCAPE_TICKS = 90;
/** After running away, creatures leave you alone for a moment. */
export const CALM_TICKS = 60 * 5;
/** A player who isn't connected keeps defending for this long, then is taken out of the fight. */
export const AWAY_TICKS = 60 * 20;

import type { Element, JobId, StatusId } from './jobs.js';

/** 'skill' carries an ability id (shared/jobs.ts): weapon skills, spells and job abilities. */
export type Cmd = 'attack' | 'skill' | 'item' | 'defend' | 'run';
/** How long someone who fell can wait for a Raise before they go home by themselves (ticks: 3 minutes). */
export const FALLEN_TICKS = 60 * 180;
/** Going home after falling: Weakened (less health and magic) for this long (ms), this much less. */
export const WEAK_MS = 3 * 60_000, WEAK_K = 0.75;
export const POTION_HEAL = 30;
/** Defending also gathers your thoughts: a little magic back. */
export const DEFEND_MP = 0.08;

/** How the fight began changes the opening and how easy it is to get away. */
export type Opening = 'first' | 'ambush' | 'even';

/** The chance (0..1) that the party gets away, when everyone has chosen Run. */
export function runChance(o: { partyLevel: number; foeLevel: number; opening: Opening; tries: number; boss: boolean }): number {
  if (o.boss) return 0;
  const base = 0.5 + (o.partyLevel - o.foeLevel) * 0.05 + (o.opening === 'first' ? 0.25 : o.opening === 'ambush' ? -0.2 : 0) + o.tries * 0.15;
  return Math.max(0.1, Math.min(0.97, base));
}

// ---------- formations: the party leader picks one before the fight ----------
export type FormationId = 'line' | 'wall' | 'pincer' | 'surround';
export interface FormationDef { id: FormationId; name: string; min: number; icon: string; desc: string; perks: string[] }
export const FORMATIONS: FormationDef[] = [
  { id: 'line', name: 'Line', min: 1, icon: '▮▮▮', desc: 'Side by side, facing the enemy. Steady and simple.', perks: ['No strengths, no weaknesses'] },
  { id: 'wall', name: 'Shield Wall', min: 2, icon: '▮▮ ▯▯', desc: 'A front row guards a back row.', perks: ['Creatures can only reach the front row', 'Front row takes 30% less damage', 'Back row heals 50% more'] },
  { id: 'pincer', name: 'Pincer', min: 2, icon: '▮ ◆ ▮', desc: 'Split around the enemy, left and right.', perks: ['Chains that switch sides hit 20% harder', 'Creatures turn to one side at a time'] },
  { id: 'surround', name: 'Surround', min: 4, icon: '◌◆◌', desc: 'Everyone circles the enemy. All in.', perks: ['Party attacks +10%', 'Creatures deal 15% less', 'Harder to run away (−20%)'] },
];
export const formationDef = (id: string | undefined) => FORMATIONS.find(f => f.id === id) ?? FORMATIONS[0];
/** The formation a party of this size actually uses (too few for the one picked: Line). */
export const formationFor = (id: FormationId, size: number): FormationId => size >= formationDef(id).min ? id : 'line';

// ---------- skill chains: different members hitting one after another, before a creature acts ----------
export const CHAIN_BONUS = [0, 0, 0.25, 0.5, 0.75, 1.0, 1.25];
export const CHAIN_NAMES = ['', '', 'Double Up', 'Triple Rhythm', 'Quad Bloom', 'Five Star', 'Grand Finale'];
export const chainBonus = (n: number) => CHAIN_BONUS[Math.min(n, CHAIN_BONUS.length - 1)];

export interface Fighter {
  id: number;                       // the entity id in the world
  side: 'party' | 'foe';
  name: string; lv: number;
  hp: number; max: number;
  atb: number;                      // 0..ATB_FULL
  state: 'ok' | 'down' | 'run' | 'defend';
  mob?: string;                     // creatures: which kind
  helper?: boolean;                 // joined from outside the party, through the help flag
  away?: boolean;                   // not connected (on autopilot)
  pos?: 'front' | 'back' | 'left' | 'right' | 'ring';   // where the formation puts them
  job?: JobId;                      // players: the job they're fighting as
  mp?: number; maxMp?: number;
  st?: [StatusId, number][];        // statuses and turns left
  cd?: Record<string, number>;      // abilities still cooling down (own turns left)
  auto?: boolean;                   // on auto-battle
  charge?: string;                  // a boss: the big move it announced, coming next
  cast?: { name: string; left: number; total: number };   // casting a spell right now (ticks)
  escape?: number;                  // standing in the gate: how far along (0..1)
  phase?: number;                   // a boss: its phase (1, 2...; left out for the first)
}
/** A big move's shape on the arena floor: everyone inside when it lands is hit. */
export interface TeleView { id: number; from: number; name: string; x: number; y: number; r: number; left: number; total: number; /** the arena's own (rocks, eruptions): from the sky, not a creature */ hazard?: 'rocks' | 'quake' }
export interface BattleView {
  id: number;
  /** The arena (zone id) everyone is in, its glowing gate (pixels), and whether the gate is sealed (a boss). */
  arena: string; exit: [number, number] | null;
  /** Shapes on the floor, and fighters (players) whose bars are full and may act now. */
  tele: TeleView[]; ready: number[];
  zone: string; x: number; y: number;   // where the circle is (pixels), for the backdrop
  fighters: Fighter[];
  turn: number | null;              // whose menu is open (only one at a time), if anyone's
  turnLeft: number;                 // seconds left to pick
  help: boolean;                    // strangers may join
  wait: boolean;                    // the bar is paused (someone is choosing)
  runners: number;                  // party members who chose Run...
  needRun: number;                  // ...out of this many still standing
  runOdds: number;                  // the chance once everyone has
  formation: FormationId;           // what the party is actually using
  chain: number;                    // the current skill chain (0 or 1: none)
  /** A horde challenge: its name, which wave of how many, and the closing ring (pixels: centre and radius). */
  horde?: { name: string; wave: number; waves: number; ring?: [number, number, number] };
}
/** One thing that happened, for the battle screen to play out. */
export type BattleEvent =
  | { k: 'hit'; from: number; to: number; amt: number; crit?: boolean; skill?: string; el?: Element; weak?: 'weak' | 'resist'; burst?: boolean }
  | { k: 'miss'; from: number; to: number }
  | { k: 'heal'; from: number; to: number; amt: number; item?: string; skill?: string; crit?: boolean }
  | { k: 'use'; from: number; skill: string; name: string; to: number[] }           // someone uses an ability (a creature too)
  | { k: 'status'; to: number; id: StatusId; on: boolean }
  | { k: 'tick'; who: number; id: 'poison' | 'regen'; amt: number }
  | { k: 'skip'; who: number; why: 'sleep' | 'stun' }
  | { k: 'burst'; name: string; bonus: number }
  | { k: 'steal'; from: number; to: number; item: string | null }
  | { k: 'revive'; from: number; to: number; amt: number }
  | { k: 'mp'; who: number; amt: number }
  | { k: 'defend'; who: number }
  | { k: 'run'; who: number; on: boolean }
  | { k: 'flee'; ok: boolean; odds: number }
  | { k: 'down'; who: number }
  | { k: 'join'; who: number; helper: boolean }
  | { k: 'leave'; who: number }
  | { k: 'say'; text: string }
  | { k: 'chain'; n: number; name: string; bonus: number }
  | { k: 'telegraph'; from: number; skill: string; name: string; text: string }   // a boss winds up a big move (it lands next turn)
  | { k: 'phase'; who: number; name: string; text: string }                       // a boss changes
  | { k: 'cast'; who: number; skill: string; name: string }                        // a spell begins (it lands with a 'use')
  | { k: 'interrupt'; who: number; name: string }                                  // ...or was broken off by moving
  | { k: 'push'; who: number; from: number; px: number }                       // knocked back (pixels): a heavy hit, a gust
  | { k: 'land'; who: number }                                                  // a big creature drops in: the ground shakes and a gust knocks everyone back
  | { k: 'back'; from: number; to: number }                                        // a hit from behind
  | { k: 'dash'; who: number; to: number }                                         // dashing in to strike
  | { k: 'escape'; who: number };                                                  // got away through the gate

/** A circle as everyone in the zone sees it. */
export interface CircleView { id: number; x: number; y: number; r: number; open: boolean; count: number;
  /** For everyone left on the field while the fight happens in its arena: who's leading it (drawn brawling with the
   *  creature in a dust cloud), and what they're up against. */
  lead?: { name: string; look: unknown; job: string }; foe?: string; foes?: number;
  /** How it ended (sent once, as the circle goes): the field shows the winner come out of the cloud. */
  done?: 'win' | 'lose' | 'fled' }

/** The circle a point (pixels) is inside of, if any. */
export const inCircle = (circles: CircleView[], x: number, y: number, pad = 0) => circles.find(c => (x - c.x) ** 2 + (y - c.y) ** 2 < (c.r + pad) ** 2) ?? null;

/** Keep someone out of the circles they may not enter: put them just outside the edge. The server and your own
 *  screen both do this after every step, the same way, so walking into a circle feels like a soft wall. */
export function pushOutOfCircles(m: { x: number; y: number; z: number; flying: boolean }, circles: { x: number; y: number; r: number }[], fits: (x: number, y: number, z: number, flying: boolean) => boolean, sub = 64): boolean {
  let moved = false;
  for (const c of circles) {
    const dx = m.x / sub - c.x, dy = m.y / sub - c.y;
    if (dx * dx + dy * dy >= c.r * c.r) continue;
    const d = Math.hypot(dx, dy) || 1, nx = Math.round((c.x + dx / d * (c.r + 1)) * sub), ny = Math.round((c.y + dy / d * (c.r + 1)) * sub);
    if (fits(nx, ny, m.z, m.flying)) { m.x = nx; m.y = ny; moved = true; }
  }
  return moved;
}
